declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

type EnvMap = Record<string, string | undefined>;
const BUILD_NODE_ENV = process.env.NODE_ENV;

function getProcessEnv(): EnvMap {
  return typeof process !== 'undefined' ? (process.env as EnvMap) : {};
}

export function getRuntimeEnv(): EnvMap {
  const processEnv = getProcessEnv();
  const runtimeEnv = typeof window !== 'undefined' && window.__ENV__ ? window.__ENV__ : {};
  // Keep build-time values like NODE_ENV while allowing runtime REACT_APP_* overrides.
  return { ...processEnv, ...runtimeEnv };
}

export function getRuntimeEnvValue(key: string): string {
  return (getRuntimeEnv()[key] || '').trim();
}

export function isProductionEnv(): boolean {
  // NODE_ENV is a build-time constant in CRA bundles.
  return BUILD_NODE_ENV === 'production';
}

export function isDevelopmentEnv(): boolean {
  return !isProductionEnv();
}

export function getSessionItem(key: string): string | null {
  return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
}

export function setSessionItem(key: string, value: string): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(key, value);
  }
}

export function removeSessionItem(key: string): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(key);
  }
}

export function getConfiguredApiBaseUrl(): string {
  return getRuntimeEnvValue('REACT_APP_API_BASE_URL');
}

export function getPortalApiBaseUrl(): string {
  return getRuntimeEnvValue('REACT_APP_PORTAL_API_BASE_URL').replace(/\/+$/, '');
}

export function getApiBaseUrl(options?: { fallbackToOrigin?: boolean }): string {
  const fallbackToOrigin = options?.fallbackToOrigin !== false;
  const sessionBaseUrl = (getSessionItem('api-base-url') || '').trim();
  if (sessionBaseUrl) return sessionBaseUrl;

  const envBaseUrl = getConfiguredApiBaseUrl();
  if (envBaseUrl) return envBaseUrl;

  if (fallbackToOrigin && typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

export function getAuthStorageRaw(): string | null {
  return getSessionItem('auth-storage');
}

export function getSmVersion(): string {
  return getRuntimeEnvValue('REACT_APP_SM_VERSION');
}

export function getUiVersion(): string {
  return (process.env.REACT_APP_UI_VERSION || '').trim();
}

export type LoginMode = 'direct' | 'cloud';

export function getLoginMode(): LoginMode {
  // Ignore inline # comments (e.g. .env lines like `REACT_APP_LOGIN_MODE=cloud # note`)
  const raw = getRuntimeEnvValue('REACT_APP_LOGIN_MODE').split('#')[0].trim().toLowerCase();
  if (raw === 'cloud') return 'cloud';
  return 'direct';
}

export function getMainRegion(): string {
  return getRuntimeEnvValue('REACT_APP_MAIN_REGION');
}

export function getNodeImageName(): string {
  return getRuntimeEnvValue('REACT_APP_NODE_IMAGE_NAME');
}

export function getRouterBasename(): string {
  const raw = getRuntimeEnvValue('REACT_APP_BASENAME');
  if (!raw) return '';
  const withLeading = raw.startsWith('/') ? raw : `/${raw}`;
  return withLeading.replace(/\/+$/, '');
}

export function getApiTimeoutMs(defaultValue = 30000): number {
  const parsed = parseInt(getRuntimeEnvValue('REACT_APP_API_TIMEOUT') || `${defaultValue}`, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultValue;
}
